import { Stack, Typography } from "@mui/material";
import { alpha, styled, type Theme } from "@mui/material/styles";

import { type INewsSentiment, NewsSentimentEnum } from "~/feature/chart/types";

import { getTooltipSurface } from "./constants";

const onSurface = (theme: Theme) => getTooltipSurface(theme).onSurface;

const mutedColor = (theme: Theme) => alpha(onSurface(theme), 0.6);

const sentimentColor = (theme: Theme, sentiment: INewsSentiment) => {
  const isDark = theme.palette.mode === "dark";

  if (sentiment === NewsSentimentEnum.POSITIVE) {
    return isDark ? theme.palette.success.dark : theme.palette.success.main;
  }

  if (sentiment === NewsSentimentEnum.NEGATIVE) {
    return isDark ? theme.palette.error.dark : theme.palette.error.main;
  }

  return alpha(onSurface(theme), 0.22);
};

export const Body = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.75),
}));

export const Row = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(0.75),
}));

export const SentimentChip = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$sentiment",
})<{ $sentiment: INewsSentiment }>(({ $sentiment, theme }) => {
  const background = sentimentColor(theme, $sentiment);

  return {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: background,
    color:
      $sentiment === NewsSentimentEnum.NEUTRAL
        ? onSurface(theme)
        : theme.palette.getContrastText(background),
  };
});

export const Meta = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  whiteSpace: "nowrap",
  color: mutedColor(theme),
}));

export const Title = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: onSurface(theme),
}));

export const Change = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$isPositive",
})<{ $isPositive: boolean }>(({ $isPositive, theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: sentimentColor(
    theme,
    $isPositive ? NewsSentimentEnum.POSITIVE : NewsSentimentEnum.NEGATIVE
  ),
}));

export const Caption = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: mutedColor(theme),
}));

export const ReadMore = styled("a")(({ theme }) => ({
  marginLeft: "auto",
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
  textDecoration: "none",
  color: sentimentColor(theme, NewsSentimentEnum.POSITIVE),

  "&:hover": {
    textDecoration: "underline",
  },
}));
